import { useQuery } from "@tanstack/react-query";
import { PROTOCOL, senderLabel, type Channel } from "@/app/master";
import { attemptVerdict, messages, superseded, type Attempt, type MessageDetail as Detail } from "@/app/messages";
import { nameOf, useNames } from "@/app/queries";
import { Alert, Button, EmptyState, formatTime, KeyValue, MessageStatePill, Modal, Msisdn, Pill, SectionTitle, Table, TableWrap, Th, Tooltip } from "@/ui";

import { MessageRoutingTrace } from "./MessageRoutingTrace";

interface Props { id: string; channels?: Channel[]; onClose: () => void }

/** 消息详情弹窗，按设计系统的 DetailModal 排：基本信息卡与客户回执（都纵向排，内容在基本信息卡里）、提交记录（docs/04 第 8 节，#213）。
 * 运营排查用，不展示价格与结算；发给通道的就是原文（决策 D75），不再逐次显示渲染内容；路由决策按各次尝试的定格trace展开，各段仍只在接口里。 */
export function MessageDetail({ id, channels, onClose }: Props) {
  const q = useQuery({ queryKey: ["message", id], queryFn: () => messages.get(id) });
  const m = q.data?.message;
  const attempts = q.data?.attempts ?? [];
  return (
    <Modal open size="lg" title="消息详情" onClose={onClose} footer={<Button onClick={onClose}>关闭</Button>}>
      {q.error && <Alert tone="danger" title="加载失败">{q.error.message}</Alert>}
      {!m && !q.error && <EmptyState title="加载中" />}
      {m && (
        <div className="ax-detail">
          <Summary id={id} m={m} />
          <section className="ax-section">
            <SectionTitle>客户回执</SectionTitle>
            <KeyValue items={[
              { label: "回执", value: m.customer_dlr_status ? `${m.customer_dlr_status} ${m.customer_dlr_error}` : "未发", mono: true },
              { label: "回执时间", value: time(m.customer_dlr_final_at), mono: true },
              { label: "决定原因", value: ({ real: "真实结果", converted: "失败按比例返回成功", timeout: "客户回执超时", timeout_converted: "客户回执超时按比例返回成功" } as Record<string, string>)[m.customer_dlr_reason ?? ""] ?? "—" },
              ...(m.customer_dlr_policy ? [
                { label: "策略版本", value: `#${m.customer_dlr_policy.id} · v${m.customer_dlr_policy.version}` },
                { label: "截止时间", value: time(m.customer_dlr_deadline), mono: true },
              ] : []),
            ]} />
          </section>
          <MessageRoutingTrace attempts={attempts} failureTrace={m.route_failure_trace} />
          <section className="ax-section">
            <SectionTitle>提交记录（{attempts.length} 次）</SectionTitle>
            {attempts.length ? <AttemptTable attempts={attempts} channels={channels} /> : <EmptyState title="没有提交">{m.failure_reason ? `失败原因 ${m.failure_reason}` : "消息还没路由。"}</EmptyState>}
          </section>
        </div>
      )}
    </Modal>
  );
}

function time(v: string | null | undefined) {
  return v ? formatTime(v) : "—";
}

/** 表格里的时刻去掉年份，列宽放得下；完整时刻在 title 里。 */
function short(v: string | null | undefined) {
  return v ? formatTime(v).slice(5) : "—";
}

/** 基本信息卡：按运营排查的顺序纵向列出号码、状态（失败时带错误码）、运营商、提交时间、归属、计费条数、sender、内容与消息 ID。 */
function Summary({ id, m }: { id: string; m: Detail }) {
  const names = useNames(); // 系统测试账号与客户不在列表里，显示成"系统测试"（决策 D94）
  return (
    <div className="ax-summary">
      <KeyValue items={[
        { label: "电话号码", value: <Msisdn value={m.dest_msisdn} /> },
        { label: "状态", value: <><MessageStatePill state={m.status} />{m.status === "FAILED" && m.error_code && <span className="ax-mono"> {m.error_code} {m.error_name}</span>}</> },
        { label: "MCC/MNC", value: `${m.dest_mcc}/${m.dest_mnc || "*"}`, mono: true },
        { label: "提交时间", value: formatTime(m.created_at), mono: true },
        { label: "客户/账号", value: <>{names.customer(m.customer_id)} / {names.account(m.account_id)} <span className="ax-mono">#{m.account_id}</span></> },
        { label: "计费条数", value: `${m.segments}（${m.encoding}，${PROTOCOL[m.protocol]}）` },
        { label: "Sender", value: <>{m.source_addr} <span className="ax-secondary">（客户提交，实际发出的见提交记录）</span></>, mono: true },
        { label: "内容", value: <p className="ax-text-block">{m.text}</p> },
        { label: "消息 ID", value: <>{id} <Button variant="ghost" size="sm" onClick={() => void navigator.clipboard?.writeText(id)}>复制</Button></>, mono: true },
        ...(m.client_ref ? [{ label: "client_ref", value: m.client_ref, mono: true }] : []),
        ...(m.test ? [{ label: "来源", value: "后台发送测试（决策 D94）" }] : []),
      ]} />
    </div>
  );
}

const SENDER_RULE: Record<string, string> = { mapping: "映射", pattern: "正则", default: "默认替换" };

/** 这次尝试发给通道的 sender（决策 D154）：替换过的标出命中的策略与规则；策略命中却没替换的（规则没命中或正则展开后不合法）也写明原因。 */
function SentSender({ a }: { a: Attempt }) {
  const r = a.sender_rewrite;
  if (!r) return <>{senderLabel(a.rendered_sender)}</>;
  const rule = r.rule ? `${SENDER_RULE[r.rule]}${r.index ? ` 第 ${r.index} 条` : ""}` : "规则未命中";
  const title = `SID 替换策略 #${r.policy_id} v${r.policy_version}，${rule}${r.replaced ? `：${r.from} → ${senderLabel(r.to)}（TON ${r.ton}，NPI ${r.npi}）` : `，原样发送${r.error ? `：${r.error}` : ""}`}`;
  // 详情放在可聚焦的 Tooltip 里，键盘与读屏也拿得到（设计系统"提示"一节）
  return <Tooltip multiline content={title}><span tabIndex={0}>{senderLabel(a.rendered_sender)}{r.replaced ? <> <Pill tone="brand" plain>已替换</Pill></> : r.error && <> <Pill tone="warning" plain>未替换</Pill></>}</span></Tooltip>;
}

/** 每次提交一行。不是最新一次又没到终态的，是超时后被新尝试取代、再没等到回执的旧尝试，停在原状态但不再影响消息（D62 第 9 条）。 */
function AttemptTable({ attempts, channels }: { attempts: Attempt[]; channels?: Channel[] }) {
  const latest = Math.max(...attempts.map((a) => a.seq_no));
  return (
    <TableWrap>
      <Table dense>
        <thead><tr><Th numeric w={48}>次</Th><Th w={120}>通道</Th><Th w={112}>发出的 Sender</Th><Th w={160}>状态</Th><Th w={144}>提交日期</Th><Th w={144}>送达日期</Th><Th numeric w={80}>计费条数</Th><Th>错误 / 判定</Th></tr></thead>
        <tbody>
          {attempts.map((a) => {
            return (
              <tr key={a.id} title={`attempt ${a.id}，供应商 #${a.supplier_id}`}>
                <td className="ax-num">{a.seq_no}</td>
                <td>{nameOf(channels, a.channel_id)}</td>
                <td className="ax-mono"><SentSender a={a} /></td>
                <td className="ax-mono">{a.status}{superseded(a, latest) && <> <Pill tone="warning" plain>已被取代</Pill></>}</td>
                <td className="ax-mono" title={time(a.submitted_at)}>{short(a.submitted_at)}</td>
                {/* 只有送达才有送达日期；失败的终态看错误 / 判定 */}
                <td className="ax-mono" title={a.status === "DELIVERED" ? time(a.done_at) : undefined}>{a.status === "DELIVERED" ? short(a.done_at) : "—"}</td>
                <td className="ax-num">{a.upstream_segments ?? a.segments}</td>
                {/* 推定送达（dlr_error 记 no_dlr）与回执超时（failure_reason 记 dlr_timeout）只记在 attempt 上，终态是网关判定而不是上游回执，要看得出来 */}
                <td className="ax-mono">{a.upstream_error || attemptVerdict(a) || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableWrap>
  );
}
